import { Heading3 } from '@src/components/typography/Heading3';
import { ButtonType } from '@src/types';
import {
  CreateOrganizationForm,
  type CreateOrganizationType,
} from './CreateOrganizationForm';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import organizationService from '@src/api/services/OrganizationService.ts';
import { Paths } from '@src/paths';
import { useNavigate } from 'react-router-dom';
import Dialog from '@components/modals/Dialog.tsx';

type AddOrganizationDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export function AddOrganizationDialog({
  open,
  setOpen,
}: AddOrganizationDialogProps) {
  const { t } = useTranslation('table');
  const { t: tCommon } = useTranslation('common');
  const { t: tOrganizationList } = useTranslation('view', {
    keyPrefix: 'organizationList',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const [organizationData, setOrganizationData] = useState<
    Partial<CreateOrganizationType>
  >({});

  const navigate = useNavigate();

  const closeModal = useCallback(() => {
    setOpen(false);
    setError(null);
    setOrganizationData({});
  }, [setOpen, setError, setOrganizationData]);

  const clearOrganizationDataForCreation = useCallback(
    (
      organizationData: Partial<CreateOrganizationType>,
    ): Omit<Partial<CreateOrganizationType>, 'country'> & {
      country?: string;
    } => ({ ...organizationData, country: organizationData.country?.id }),
    [],
  );

  const createOrganization = useCallback(async () => {
    setLoading(true);
    try {
      const clearedOrganizationData =
        clearOrganizationDataForCreation(organizationData);
      const { id: newOrganizationId } =
        await organizationService.createOrganization(clearedOrganizationData);
      navigate(`${Paths.ORGANIZATIONS}/${newOrganizationId}`);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(t('errors.unknown', { ns: 'common' }));
      }
    } finally {
      setLoading(false);
    }
  }, [clearOrganizationDataForCreation, navigate, organizationData, t]);

  return (
    <Dialog
      open={open}
      setOpen={setOpen}
      dismissible={false}
      actions={[
        {
          buttonProps: {
            buttonType: ButtonType.PRIMARY,
            type: 'submit',
            onClick: () => void createOrganization(),
            label: tCommon('button.confirm'),
            testId: 'confirm-button',
            loading: loading,
            disabled: !formIsValid,
          },
        },
        {
          buttonProps: {
            buttonType: ButtonType.SECONDARY,
            onClick: () => closeModal(),
            label: tCommon('button.cancel'),
            testId: 'cancel-button',
            loading: loading,
          },
        },
      ]}
    >
      <div className="flex flex-col justify-center gap-6 sm:mb-12">
        <Heading3 className="text-center">
          {tOrganizationList('addOrganization')}
        </Heading3>
        {error && (
          <div className="text-orange-600 my-4">
            {t('errors.errorWhileSaving', {
              errorMessage: error,
              ns: 'common',
            })}
          </div>
        )}
        <div>
          <CreateOrganizationForm
            organizationData={organizationData}
            setOrganizationData={setOrganizationData}
            setFormIsValid={setFormIsValid}
          />
        </div>
      </div>
    </Dialog>
  );
}
